/**
 * useChecklistImport.js
 *
 * Hook for managing the checklist import wizard state and operations.
 * Coordinates between UI steps and extraction service.
 */

import { useState, useCallback, useMemo } from 'react';
import { useAIExtraction } from './useAIExtraction';
import {
  addItem,
  updateItem,
  removeItem,
  reorderItems,
  detectConflicts,
  mapToChecklistSchema,
  generateFileHash
} from '../services/ai/extractionService';
import { IMPORT_WIZARD_STEPS, IMPORT_METADATA_SCHEMA } from '../config/operations/checklistImportConfig';

/**
 * Calculate how many items were modified from original extraction
 */
const calculateModifiedCount = (original = [], edited = []) => {
  if (!original.length) return edited.length;

  let modified = 0;
  edited.forEach((editedItem) => {
    const originalItem = original.find(o => o.id === editedItem.id);
    if (!originalItem) {
      modified++; // New item
    } else if (originalItem.text !== editedItem.text || originalItem.required !== editedItem.required) {
      modified++; // Modified
    }
  });

  // Count removed items
  original.forEach((originalItem) => {
    if (!edited.find(e => e.id === originalItem.id)) {
      modified++;
    }
  });

  return modified;
};

/**
 * Hook for managing the complete import wizard flow
 */
export const useChecklistImport = () => {
  // Current step in wizard
  const [currentStep, setCurrentStep] = useState('select_type');

  // User selections
  const [checklistType, setChecklistType] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);

  // Extraction state
  const [extractionResult, setExtractionResult] = useState(null);
  const [editedItems, setEditedItems] = useState([]);

  // Conflict detection
  const [conflicts, setConflicts] = useState([]);
  const [existingItems, setExistingItems] = useState([]);

  // Approval data
  const [approvalData, setApprovalData] = useState({
    approverName: '',
    approverRole: '',
    documentRef: '',
    signature: null
  });

  // General state
  const [error, setError] = useState(null);

  // AI extraction hook
  const {
    extractItems,
    isProcessing,
    error: extractionError
  } = useAIExtraction();

  // ============================================================================
  // STEP NAVIGATION
  // ============================================================================

  const currentStepIndex = useMemo(() => {
    return IMPORT_WIZARD_STEPS.findIndex(s => s.id === currentStep);
  }, [currentStep]);

  /**
   * Check if user can proceed to next step
   */
  const canProceed = useCallback(() => {
    switch (currentStep) {
      case 'select_type':
        return !!checklistType;

      case 'upload':
        return !!uploadedFile;

      case 'processing':
        return !isProcessing && extractionResult && !extractionError;

      case 'review':
        return editedItems.length > 0;

      case 'warnings':
        return true; // Can always proceed past warnings

      case 'approval':
        return !!(
          approvalData.approverName &&
          approvalData.approverRole &&
          approvalData.signature
        );

      case 'confirm':
        return true;

      default:
        return false;
    }
  }, [currentStep, checklistType, uploadedFile, isProcessing, extractionResult, extractionError, editedItems, approvalData]);

  // ============================================================================
  // EXTRACTION (must be defined before goToNextStep)
  // ============================================================================

  /**
   * Start the AI extraction process
   */
  const startExtraction = useCallback(async () => {
    if (!uploadedFile || !checklistType) {
      setError('Missing file or checklist type');
      return;
    }

    setError(null);

    try {
      const result = await extractItems(uploadedFile, checklistType);

      if (result.success) {
        setExtractionResult(result.data);
        setEditedItems(result.data.items || []);

        // Auto-advance to review step
        setCurrentStep('review');
      } else {
        setError(result.error || 'Extraction failed');
      }
    } catch (err) {
      setError(err.message || 'Extraction failed');
    }
  }, [uploadedFile, checklistType, extractItems]);

  // ============================================================================
  // STEP NAVIGATION (continued)
  // ============================================================================

  /**
   * Move to next step
   */
  const goToNextStep = useCallback(async () => {
    const currentIndex = IMPORT_WIZARD_STEPS.findIndex(s => s.id === currentStep);
    if (currentIndex < IMPORT_WIZARD_STEPS.length - 1) {
      const nextStep = IMPORT_WIZARD_STEPS[currentIndex + 1].id;

      // Handle processing step transition
      if (currentStep === 'upload' && nextStep === 'processing') {
        setCurrentStep(nextStep);
        await startExtraction();
        return;
      }

      // Handle conflict detection before warnings step
      if (currentStep === 'review' && nextStep === 'warnings') {
        const detectedConflicts = detectConflicts(editedItems, existingItems);
        setConflicts(detectedConflicts);
      }

      setCurrentStep(nextStep);
    }
  }, [currentStep, editedItems, existingItems, startExtraction]);

  /**
   * Move to previous step
   */
  const goToPreviousStep = useCallback(() => {
    const currentIndex = IMPORT_WIZARD_STEPS.findIndex(s => s.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(IMPORT_WIZARD_STEPS[currentIndex - 1].id);
    }
  }, [currentStep]);

  /**
   * Go to specific step
   */
  const goToStep = useCallback((stepId) => {
    const stepIndex = IMPORT_WIZARD_STEPS.findIndex(s => s.id === stepId);
    if (stepIndex !== -1) {
      setCurrentStep(stepId);
    }
  }, []);

  // ============================================================================
  // ITEM MANIPULATION
  // ============================================================================

  /**
   * Update an item
   */
  const handleUpdateItem = useCallback((itemId, updates) => {
    setEditedItems(prev => updateItem(prev, itemId, updates));
  }, []);

  /**
   * Add a new item
   */
  const handleAddItem = useCallback((newItem) => {
    setEditedItems(prev => addItem(prev, newItem));
  }, []);

  /**
   * Remove an item
   */
  const handleRemoveItem = useCallback((itemId) => {
    setEditedItems(prev => removeItem(prev, itemId));
  }, []);

  /**
   * Reorder items
   */
  const handleReorderItems = useCallback((fromIndex, toIndex) => {
    setEditedItems(prev => reorderItems(prev, fromIndex, toIndex));
  }, []);

  // ============================================================================
  // FINALIZATION
  // ============================================================================

  /**
   * Finalize the import
   */
  const finalizeImport = useCallback(async () => {
    if (!canProceed()) {
      return { success: false, error: 'Not ready to finalize' };
    }

    try {
      // Generate file hash for audit trail
      const fileHash = await generateFileHash(uploadedFile);

      // Build metadata
      const metadata = {
        ...IMPORT_METADATA_SCHEMA,
        id: `imported-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        importedAt: new Date().toISOString(),
        importSource: uploadedFile.name,
        isCustom: true,

        importedBy: {
          id: 'current-user', // Would come from auth context
          name: approvalData.approverName,
          role: approvalData.approverRole
        },

        sourceDocument: {
          reference: approvalData.documentRef || '',
          revision: '',
          date: new Date().toISOString(),
          hash: fileHash
        },

        extraction: {
          method: 'ai_vision',
          modelVersion: extractionResult?.model || 'claude-3-haiku',
          itemsExtracted: extractionResult?.data?.items?.length || 0,
          itemsModifiedByUser: calculateModifiedCount(extractionResult?.data?.items, editedItems),
          confidence: extractionResult?.metadata?.overallConfidence || 0
        },

        approval: {
          required: true,
          approvedBy: approvalData.approverName,
          approvedAt: new Date().toISOString(),
          signature: approvalData.signature
        }
      };

      // Map to final checklist schema
      const finalChecklist = mapToChecklistSchema(
        { ...extractionResult, items: editedItems },
        checklistType,
        metadata
      );

      // Save to storage (would use appropriate storage hook)
      // For now, return the result for the caller to handle
      return {
        success: true,
        checklist: finalChecklist,
        metadata
      };
    } catch (err) {
      return {
        success: false,
        error: err.message || 'Failed to finalize import'
      };
    }
  }, [canProceed, uploadedFile, approvalData, extractionResult, editedItems, checklistType]);

  // ============================================================================
  // RESET
  // ============================================================================

  /**
   * Reset wizard state
   */
  const reset = useCallback(() => {
    setCurrentStep('select_type');
    setChecklistType(null);
    setUploadedFile(null);
    setExtractionResult(null);
    setEditedItems([]);
    setConflicts([]);
    setApprovalData({
      approverName: '',
      approverRole: '',
      documentRef: '',
      signature: null
    });
    setError(null);
  }, []);

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // State
    currentStep,
    currentStepIndex,
    checklistType,
    uploadedFile,
    extractionResult,
    editedItems,
    conflicts,
    approvalData,
    isProcessing,
    error: error || extractionError,

    // Setters
    setChecklistType,
    setUploadedFile,
    setApprovalData,
    setExistingItems,

    // Item manipulation
    updateItem: handleUpdateItem,
    addItem: handleAddItem,
    removeItem: handleRemoveItem,
    reorderItems: handleReorderItems,

    // Navigation
    goToNextStep,
    goToPreviousStep,
    goToStep,
    canProceed,

    // Operations
    startExtraction,
    finalizeImport,
    reset
  };
};

export default useChecklistImport;
